/**
 * ImageGalleryV2
 *
 * Resource-based gallery: accepts pre-computed pimImages / Cylindo props directly,
 * instead of deriving everything from a product object (V1 pattern).
 *
 * Display priority (resolver supplies at most one of PIM carousel or Cylindo 3D):
 *   1. pimImages present      → Swiper carousel (images + videos)
 *   2. cylindoId + customerId → CylindoViewer 3D
 *   3. Neither                → Placeholder SVG
 *
 * UI is identical to ImageGallery V1 — same theme key, same overlay buttons,
 * same unified slide height measurement, same fullscreen portal behaviour.
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image';
import {
  Box,
  HStack,
  IconButton,
  Skeleton as ChakraSkeleton,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import CylindoARLink from '@amblique/king-living/app/components/cylindo-ar-link';
import CylindoViewer from '@amblique/king-living/app/components/cylindo-viewer';
import GalleryVideoSlide from '@amblique/king-living/app/components/gallery-video-slide';
import {
  ArIcon,
  CloseIcon,
  CylindoZoomIcon,
  CylindoZoomOutIcon,
  DragToRotateIcon,
  FullscreenIcon,
} from '@amblique/king-living/app/components/icons';
import {useCylindoAR} from '@amblique/king-living/app/hooks/use-cylindo-availability';
import {useVisualViewportRect} from '@amblique/king-living/app/hooks/use-visual-viewport-rect';
import {
  buildGalleryMediaKey,
  updateSwiperPreservingSlide,
} from '@amblique/king-living/app/utils/gallery-swiper-utils';
import {mergeCylindoConfiguration} from '@amblique/king-living/app/utils/cylindo-params';
import {buildDynamicImageSrc} from '@amblique/king-living/app/utils/responsive-image-src';

/** theme breakpoints.xl — desktop gallery min-height cap */
const DESKTOP_MIN_WIDTH_PX = 1280;

/**
 * Controls passed to <cylindo-viewer> when V2 manages its own AR button via CylindoARLink.
 * 'nav' retains the drag-to-rotate navigation aid on mobile; 'ar' is omitted so the
 * native Cylindo AR button is suppressed in favour of our custom-styled button.
 */
const CYLINDO_CONTROLS_DESKTOP_V2 = '';
const CYLINDO_CONTROLS_MOBILE_V2 = 'nav';
const UNIFIED_SLIDE_MIN_H_MAX_DESKTOP_PX = 600;

/** Matches hero aspect (25:12) — stable layout before images / Cylindo paint */
const GALLERY_HERO_ASPECT = 25 / 12;

/**
 * Explicit `width`/`height` HTML *attributes* for the PIM-image `<DynamicImage>` slides, expressed
 * as the 25:12 hero ratio. Satisfies the TSD "images have explicit height/width" requirement (and
 * the matching Lighthouse audit) without disturbing the responsive `width/height: 100%` CSS fill —
 * only the ratio matters to the browser here, since the slide box is sized by the gallery, not the
 * intrinsic image. Note this path only renders for products with flat PIM images; Cylindo-model
 * products render via the <cylindo-viewer> web component below, whose markup we don't control.
 */
const GALLERY_HERO_HTML_WIDTH = 2500;
const GALLERY_HERO_HTML_HEIGHT = 1200;

const GALLERY_HERO_MIN_HEIGHT = `min(calc(100vw * 12 / 25), ${UNIFIED_SLIDE_MIN_H_MAX_DESKTOP_PX}px)`;
const GALLERY_HERO_MIN_HEIGHT_CYLINDO = 'min(calc(100vw * 12 / 25), 380px)';
const CYLINDO_ZOOMED_IN_THRESHOLD = 1.05; // Drives zoom-out enablement and drag-to-rotate visibility above zoom > 1.

function withCylindoViewer(wrapperRef, fn) {
  const viewer = wrapperRef?.current?.querySelector('cylindo-viewer');

  if (viewer) fn(viewer);
}

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton
// ─────────────────────────────────────────────────────────────────────────────

export const Skeleton = ({size}) => {
  const styles = useMultiStyleConfig('ProductImageGallery', {size});

  return (
    <Box data-testid="sf-image-gallery-v2-skeleton">
      <ChakraSkeleton aspectRatio={GALLERY_HERO_ASPECT} {...styles.heroImageSkeleton} />
    </Box>
  );
};

Skeleton.propTypes = {
  size: PropTypes.string,
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const ImageGalleryV2 = ({
  cylindoErrorImageUrl,
  cylindoFeatures = [],
  cylindoId,
  customerId,
  hideOverlayButtons = false,
  lazy = false,
  pimImages,
  productName,
  showArButton = false,
  size,
}) => {
  // ─── Refs ──────────────────────────────────────────────────────────────────
  const cylindoWrapperRef = useRef(null);
  const heroSwiperRef = useRef(null);
  const swiperRef = useRef(null);
  /** Prior `realIndex` for loop-wrap mute reset. */
  const prevRealIndexForLoopMuteRef = useRef(0);
  const pimImagesForSwiperRef = useRef(pimImages);
  pimImagesForSwiperRef.current = pimImages;
  /** Skip mobile remeasure on scroll-driven ResizeObserver (iOS address bar). */
  const skipMobileRemeasureRef = useRef(false);
  // ─── State ─────────────────────────────────────────────────────────────────
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [cylindoError, setCylindoError] = useState(false);
  const [cylindoViewerReady, setCylindoViewerReady] = useState(false);
  const [isCylindoFullscreen, setIsCylindoFullscreen] = useState(false);
  const [isCylindoZoomedIn, setIsCylindoZoomedIn] = useState(false);
  const [videoMutedMap, setVideoMutedMap] = useState({});
  const [unifiedSlideMinPx, setUnifiedSlideMinPx] = useState(null);
  const [pimFirstSlideReady, setPimFirstSlideReady] = useState(false);

  // ─── Derived ───────────────────────────────────────────────────────────────
  const styles = useMultiStyleConfig('ImageGallery', {size});

  const hasPimImages = Array.isArray(pimImages) && pimImages.length > 0;
  const hasCylindo = Boolean(cylindoId && customerId);
  const pimMediaKey = useMemo(() => buildGalleryMediaKey(pimImages), [pimImages]);

  /**
   * Process cylindoFeatures → comma-separated configuration string.
   * Later items override earlier items with the same feature key (text before ':').
   * e.g. ["UPHOLSTERY:BX-02","UPHOLSTERY:AX-11","TIMBER SHELVES VENEERS:MS_CG"]
   *   →  "UPHOLSTERY:AX-11, TIMBER SHELVES VENEERS:MS_CG"
   */
  const cylindoConfiguration = useMemo(
    () => mergeCylindoConfiguration([], cylindoFeatures),
    [cylindoFeatures]
  );

  const pimImagesStableKey = useMemo(() => {
    if (!Array.isArray(pimImages) || pimImages.length === 0) return '';

    return pimImages
      .map((m) => `${m?.mediaType || 'image'}:${m?.disBaseLink || m?.link || m?.videoUrl || ''}`)
      .join('|');
  }, [pimImages]);

  /**
   * Responsive `widths` for the PIM hero <DynamicImage> → drives the `sizes` attribute (TSD
   * "Responsive Image Params" / KINDW-1836), for parity with the PLP PIM tile. Full-viewport on
   * mobile, capped at the 52.5rem (840px) hero max-width on desktop.
   *
   * Units MUST be px / vw / unit-less numbers: the vendor `convertToPxNumbers`
   * (utils/responsive-image.js) does not parse `rem`, so a rem value silently becomes `0` and
   * emits invalid `… 0w` srcSet descriptors — the original KINDW-1836 defect. We therefore express
   * the desktop cap as a px number instead of reading the rem theme token (`heroImage.maxWidth`).
   */
  const widths = useMemo(
    () => ({
      base: '100vw',
      lg: 840,
    }),
    []
  );

  // ─── Custom hooks ──────────────────────────────────────────────────────────
  const viewportRect = useVisualViewportRect(isCylindoFullscreen);

  // Responsive controls for CylindoViewer — suppresses native AR button when V2 manages it
  const v2CylindoControls = useBreakpointValue({
    base: CYLINDO_CONTROLS_MOBILE_V2,
    md: CYLINDO_CONTROLS_MOBILE_V2,
    xl: CYLINDO_CONTROLS_DESKTOP_V2,
  });

  const isCylindoVisible = !hasPimImages && hasCylindo && !cylindoError;

  // AR availability — only checks when Cylindo viewer is actually visible and showArButton is requested
  const {arqrUrl, isChecking: isCylindoArChecking} = useCylindoAR(
    isCylindoVisible && showArButton ? cylindoId : undefined,
    customerId,
    cylindoConfiguration
  );

  /** Custom overlay controls only after AR availability check (when used) and viewer `ready` — not while loading or on error. */
  const showCylindoOverlayControls =
    !hideOverlayButtons &&
    !cylindoError &&
    cylindoViewerReady &&
    (!showArButton || !isCylindoArChecking);

  // ─── Callbacks ─────────────────────────────────────────────────────────────
  /**
   * `activeSlideIndex` uses `realIndex` so it matches `map` indices and video `isActive` / `tabIndex`
   * under Swiper loop (clones). Loop wrap is detected for mute reset from the same `realIndex` steps.
   */
  const syncActiveSlideFromSwiper = useCallback((swiper) => {
    const nextReal =
      typeof swiper?.realIndex === 'number'
        ? swiper.realIndex
        : typeof swiper?.activeIndex === 'number'
          ? swiper.activeIndex
          : 0;
    setActiveSlideIndex(nextReal);

    const images = pimImagesForSwiperRef.current;
    const n = Array.isArray(images) ? images.length : 0;
    const prevReal = prevRealIndexForLoopMuteRef.current;

    if (n > 1 && images) {
      const loopedAcrossEnds =
        (prevReal === n - 1 && nextReal === 0) || (prevReal === 0 && nextReal === n - 1);
      if (loopedAcrossEnds) {
        setVideoMutedMap((old) => {
          const m = {...old};
          images.forEach((med, idx) => {
            if (med?.mediaType === 'video') {
              m[idx] = true;
            }
          });
          return m;
        });
      }
    }
    prevRealIndexForLoopMuteRef.current = nextReal;
  }, []);

  const measureUnifiedSlideHeight = useCallback(({force = false} = {}) => {
    const root = heroSwiperRef.current;

    if (!root || typeof window === 'undefined') return;

    const isDesktop = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`).matches;

    if (!isDesktop && skipMobileRemeasureRef.current && !force) {
      return;
    }

    const slides = root.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');

    if (!slides.length) return;

    let maxH = 0;

    slides.forEach((slide) => {
      const w = slide.getBoundingClientRect().width;

      if (!w) return;

      slide.querySelectorAll('img').forEach((img) => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          const h = (img.naturalHeight / img.naturalWidth) * w;

          maxH = Math.max(maxH, h);
        }
      });

      const ambVideo = slide.querySelector('.amb-video');

      if (ambVideo) {
        const vh = ambVideo.getBoundingClientRect().height || ambVideo.offsetHeight;

        maxH = Math.max(maxH, vh || (w * 9) / 16);
      }

      maxH = Math.max(maxH, slide.offsetHeight, slide.scrollHeight);
    });

    const slideW = slides[0].getBoundingClientRect().width;
    const designFloor = slideW > 0 ? (slideW * 12) / 25 : 0;
    const unified = Math.max(maxH, designFloor);

    if (Number.isFinite(unified) && unified > 0) {
      const rounded = Math.ceil(unified);

      setUnifiedSlideMinPx(
        isDesktop ? Math.min(rounded, UNIFIED_SLIDE_MIN_H_MAX_DESKTOP_PX) : rounded
      );

      if (!isDesktop) {
        skipMobileRemeasureRef.current = true;
      }
    }
  }, []);

  const scheduleMeasureUnifiedHeight = useCallback(({force = false} = {}) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => measureUnifiedSlideHeight({force}));
    });
  }, [measureUnifiedSlideHeight]);

  const heroSwiperOptions = useMemo(
    () => ({
      threshold: 8,
      touchStartPreventDefault: false,
      onSlideChange(swiper) {
        syncActiveSlideFromSwiper(swiper);
        scheduleMeasureUnifiedHeight({force: true});
      },
      onSwiper(swiper) {
        swiperRef.current = swiper;
        syncActiveSlideFromSwiper(swiper);
      },
    }),
    [scheduleMeasureUnifiedHeight, syncActiveSlideFromSwiper]
  );

  const handleZoomClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    withCylindoViewer(cylindoWrapperRef, (v) => {
      v.zoom = Math.min((Number(v.zoom) || 1) + 0.5, 3);
      setIsCylindoZoomedIn(true);
    });
  }, []);

  const handleZoomOutClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    withCylindoViewer(cylindoWrapperRef, (v) => {
      const next = Math.max((Number(v.zoom) || 1) - 0.5, 1);

      if (next <= CYLINDO_ZOOMED_IN_THRESHOLD) {
        v.zoomStop?.();
        setIsCylindoZoomedIn(false);
      } else {
        v.zoom = next;
      }
    });
  }, []);

  const onCylindoZoomChange = useCallback((level) => {
    setIsCylindoZoomedIn(level > CYLINDO_ZOOMED_IN_THRESHOLD);
  }, []);

  const handleOpenFullscreen = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsCylindoFullscreen(true);
  }, []);

  const handleCloseFullscreen = useCallback(() => {
    setIsCylindoFullscreen(false);
  }, []);

  const handleVideoMuteChange = useCallback((index, muted) => {
    setVideoMutedMap((prev) => ({...prev, [index]: muted}));
  }, []);

  const onCylindoError = useCallback(() => setCylindoError(true), []);

  const onCylindoReady = useCallback(() => setCylindoViewerReady(true), []);

  const handleArClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    withCylindoViewer(cylindoWrapperRef, (v) => {
      v.ar = true;
    });
  }, []);

  // ─── Effects ───────────────────────────────────────────────────────────────
  // Reset to first slide only when slide URLs change (not array reference).
  useEffect(() => {
    prevRealIndexForLoopMuteRef.current = 0;
    if (swiperRef.current) {
      swiperRef.current.slideTo(0);
      setActiveSlideIndex(0);
    }
  }, [pimMediaKey]);

  useLayoutEffect(() => {
    skipMobileRemeasureRef.current = false;
    setUnifiedSlideMinPx(null);
    scheduleMeasureUnifiedHeight({force: true});
  }, [pimMediaKey, scheduleMeasureUnifiedHeight]);

  useEffect(() => {
    const root = heroSwiperRef.current;

    if (!root) return;

    const ro = new ResizeObserver(() => scheduleMeasureUnifiedHeight());

    ro.observe(root);
    window.addEventListener('resize', scheduleMeasureUnifiedHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', scheduleMeasureUnifiedHeight);
    };
  }, [pimMediaKey, scheduleMeasureUnifiedHeight]);

  useEffect(() => {
    if (unifiedSlideMinPx != null && swiperRef.current) {
      updateSwiperPreservingSlide(swiperRef.current);
    }
  }, [unifiedSlideMinPx]);

  // Lock body scroll while Cylindo fullscreen is open.
  useEffect(() => {
    if (!isCylindoFullscreen || typeof document === 'undefined') return;

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('touch-action');
    };
  }, [isCylindoFullscreen]);

  // Reset Cylindo error / ready ONLY when the underlying viewer instance
  // changes (i.e. cylindoId / customerId). Configuration changes (option-product
  // selection swaps) are handled smoothly by the <cylindo-viewer> web component
  // itself — it cross-fades to the new frame without re-firing `ready`. Resetting
  // here on every cylindoConfiguration change would make the wrapper re-arm its
  // safety-net timer (see CylindoViewer notes) and incorrectly flip to
  // "Image not available" after the timeout. Mirrors the V1 ImageGallery pattern.
  useEffect(() => {
    setCylindoError(false);
    setCylindoViewerReady(false);
    setIsCylindoZoomedIn(false);
  }, [cylindoId, customerId]);

  // PIM: reset first-slide readiness when gallery media set changes.
  useEffect(() => {
    setPimFirstSlideReady(false);
  }, [pimImagesStableKey]);

  // PIM: first slide is video — no image onLoad; hide skeleton after short settle (iframe/player mount).
  useEffect(() => {
    const first = pimImages?.[0];

    if (!first || first.mediaType !== 'video') return;

    const id = window.setTimeout(() => setPimFirstSlideReady(true), 500);

    return () => window.clearTimeout(id);
  }, [pimImagesStableKey]);

  // ─── Render ────────────────────────────────────────────────────────────────
  const loadingStrategy = lazy ? 'lazy' : 'eager';

  // ── 1. Cylindo 3D ──────────────────────────────────────────────────────────
  if (isCylindoVisible) {
    return (
      <Box
        ref={cylindoWrapperRef}
        aria-busy={!cylindoViewerReady}
        aria-label={`3D viewer${productName ? ` for ${productName}` : ''}`}
        className="cylindo-viewer-wrapper"
        role="region"
        {...styles.cylindoViewerWrapper}
        sx={{
          minH: GALLERY_HERO_MIN_HEIGHT_CYLINDO,
          ...(styles.cylindoViewerWrapper?.sx || {}),
        }}
      >
        {!cylindoViewerReady && (
          <Box
            inset={0}
            minH={GALLERY_HERO_MIN_HEIGHT_CYLINDO}
            pointerEvents="none"
            position="absolute"
            width="100%"
            zIndex={0}
            aria-hidden
          >
            <Skeleton size={size} />
          </Box>
        )}
        {showCylindoOverlayControls && (
          <>
            {!isCylindoZoomedIn && (
              <HStack aria-hidden="true" {...styles.cylindoOverlayLeft}>
                <Box {...styles.cylindoDragToRotateBadge}>
                  <DragToRotateIcon {...styles.cylindoDragToRotateIcon} />
                  <Text {...styles.cylindoDragToRotateText}>
                    <FormattedMessage
                      defaultMessage="Drag to rotate"
                      id="product_image_gallery.cylindo.drag_to_rotate"
                    />
                  </Text>
                </Box>
              </HStack>
            )}

            <HStack
              aria-label="3D viewer controls"
              role="toolbar"
              spacing={2}
              {...styles.cylindoOverlayRight}
            >
              {arqrUrl && (
                <CylindoARLink
                  arqrUrl={arqrUrl}
                  productName={productName}
                  buttonProps={{
                    ...styles.cylindoArButton,
                    leftIcon: <ArIcon />,
                  }}
                  tabIndex={0}
                  onMobileClick={handleArClick}
                />
              )}
              <IconButton
                aria-label="Zoom image"
                icon={<CylindoZoomIcon {...styles.cylindoZoomIcon} />}
                size="sm"
                variant="outline"
                tabIndex={0}
                {...styles.cylindoZoomButton}
                onClick={handleZoomClick}
              />
              <IconButton
                aria-label="Zoom out image"
                icon={<CylindoZoomOutIcon {...styles.cylindoZoomIcon} />}
                isDisabled={!isCylindoZoomedIn}
                size="sm"
                variant="outline"
                tabIndex={0}
                {...styles.cylindoZoomButton}
                onClick={handleZoomOutClick}
              />
            </HStack>

            <IconButton
              aria-label="Fullscreen view"
              icon={<FullscreenIcon {...styles.cylindoFullscreenIcon} />}
              size="sm"
              variant="outline"
              tabIndex={0}
              {...styles.cylindoFullscreenButton}
              onClick={handleOpenFullscreen}
            />
          </>
        )}

        <Box position="relative" zIndex={1}>
          <CylindoViewer
            code={cylindoId}
            configuration={cylindoConfiguration}
            controls={showArButton ? v2CylindoControls : undefined}
            customerId={customerId}
            onError={onCylindoError}
            onReady={onCylindoReady}
            onZoomChange={onCylindoZoomChange}
          />
        </Box>

        {/* Fullscreen portal — rendered at document.body to escape all stacking contexts */}
        {isCylindoFullscreen &&
          typeof document !== 'undefined' &&
          createPortal(
            <Box
              bg="white"
              display="flex"
              flexDirection="column"
              position="fixed"
              zIndex={9999}
              {...(viewportRect
                ? {
                  height: `${viewportRect.height}px`,
                  left: `${viewportRect.left}px`,
                  top: `${viewportRect.top}px`,
                  width: `${viewportRect.width}px`,
                }
                : {
                  bottom: 0,
                  left: 0,
                  right: 0,
                  top: 0,
                })}
            >
              <IconButton
                aria-label="Close fullscreen"
                color="gray.900"
                position="absolute"
                right={3}
                size="md"
                top={3}
                variant="ghost"
                zIndex={1}
                _active={{color: 'gray.900'}}
                _hover={{color: 'gray.900'}}
                icon={<CloseIcon boxSize="20px" />}
                onClick={handleCloseFullscreen}
              />
              <Box alignItems="center" display="flex" flex={1} justifyContent="center">
                <CylindoViewer
                  code={cylindoId}
                  configuration={cylindoConfiguration}
                  customerId={customerId}
                />
              </Box>
            </Box>,
            document.body
          )}
      </Box>
    );
  }

  // ── 2. PIM images carousel ─────────────────────────────────────────────────
  if (hasPimImages) {
    return (
      <Box
        ref={heroSwiperRef}
        aria-busy={!pimFirstSlideReady}
        aria-label={`Image gallery${productName ? ` for ${productName}` : ''}`}
        position="relative"
        role="region"
        sx={{
          '--hero-gallery-slide-min-h':
            unifiedSlideMinPx != null ? `${unifiedSlideMinPx}px` : undefined,
          '--hero-gallery-slide-max-h': UNIFIED_SLIDE_MIN_H_MAX_DESKTOP_PX + 'px',
          ...(styles.heroSwiper?.sx || {}),
        }}
      >
        {!pimFirstSlideReady && (
          <Box
            inset={0}
            minH={GALLERY_HERO_MIN_HEIGHT}
            pointerEvents="none"
            position="absolute"
            width="100%"
            zIndex={2}
            aria-hidden
          >
            <Skeleton size={size} />
          </Box>
        )}
        <Swiper
          className="product-gallery product-gallery-v2"
          options={heroSwiperOptions}
          presets="product_gallery_complex"
        >
          {pimImages.map((media, index) => {
            if (media.mediaType === 'video') {
              return (
                <GalleryVideoSlide
                  key={`video-${media.videoUrl || index}`}
                  alt={media.alt || `Product video ${index + 1}`}
                  containerProps={styles.videoSlide}
                  isActive={activeSlideIndex === index}
                  isMuted={videoMutedMap[index] ?? true}
                  muteButtonProps={styles.videoMuteButton}
                  muteControlProps={styles.videoMuteControl}
                  playPauseButtonProps={styles.videoPlayPauseButton}
                  playPauseControlProps={styles.videoPlayPauseControl}
                  videoUrl={media.videoUrl}
                  onMutedChange={(muted) => handleVideoMuteChange(index, muted)}
                />
              );
            }

            const heroSrc = buildDynamicImageSrc(media.disBaseLink || media.link);

            // `widths` drives the responsive `sizes` attribute (TSD "Responsive Image Params" /
            // KINDW-1836) — parity with the PLP PIM tile. These slides are always PIM images
            // (Cylindo renders via <cylindo-viewer>), and PIM is a plain proxy with no transform
            // service, so `buildDynamicImageSrc` returns the URL as-is with no `{width}` template:
            // the emitted `srcSet` therefore lists that single asset under VALID width descriptors
            // (the widths are px/vw — never rem — so no `… 0w` entries), while `sizes` still tells
            // the browser the correct layout width. CLS is handled by the explicit
            // htmlWidth/htmlHeight below. See utils/responsive-image-src.js (PIM policy).
            return (
              <DynamicImage
                key={`image-${index}`}
                src={heroSrc}
                widths={widths}
                imageProps={{
                  alt: media.alt || `Product image ${index + 1}`,
                  loading: index === 0 ? loadingStrategy : 'lazy',
                  title: media.title,
                  width: '100%',
                  height: '100%',
                  htmlWidth: GALLERY_HERO_HTML_WIDTH,
                  htmlHeight: GALLERY_HERO_HTML_HEIGHT,
                  objectFit: 'cover',
                  onError: index === 0 ? () => setPimFirstSlideReady(true) : undefined,
                  onLoad: (event) => {
                    if (index === 0) {
                      setPimFirstSlideReady(true);
                    }

                    scheduleMeasureUnifiedHeight({force: true});
                  },
                }}
              />
            );
          })}
        </Swiper>
      </Box>
    );
  }

  // ── 3. Cylindo error — custom image when Cylindo failed ────────────────────
  if (cylindoError && cylindoErrorImageUrl) {
    return (
      <Box aria-label={`Image for ${productName || 'product'}`} role="img" {...styles.placeholderWrapper}>
        <Box
          alt={productName || 'Product image'}
          as="img"
          src={cylindoErrorImageUrl}
          {...styles.placeholderImage}
        />
      </Box>
    );
  }

  // ── 4. Placeholder — no images available ───────────────────────────────────
  return (
    <Box aria-label="Image not available" role="img" {...styles.placeholderWrapper}>
      <svg
        fill="none"
        height="300"
        viewBox="0 0 400 300"
        width="400"
        xmlns="http://www.w3.org/2000/svg"
        {...styles.placeholderImage}
      >
        <rect fill="#F7F5F0" height="300" width="400" />
        <g opacity="0.35">
          <rect
            height="84"
            rx="4"
            stroke="#1B1A18"
            strokeWidth="2"
            width="104"
            x="148"
            y="85"
          />
          <circle cx="175" cy="112" r="9" stroke="#1B1A18" strokeWidth="2" />
          <path
            d="M149 153L175 127L197 149L213 133L251 169"
            stroke="#1B1A18"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </g>
        <text
          fill="rgba(27,26,24,0.40)"
          fontFamily="sans-serif"
          fontSize="14"
          fontWeight="500"
          textAnchor="middle"
          x="200"
          y="208"
        >
          Image not available
        </text>
      </svg>
    </Box>
  );
};

ImageGalleryV2.propTypes = {
  /**
   * Optional image URL shown when Cylindo fails to load and there are no PIM images.
   * Falls back to the generic "Image not available" placeholder when omitted.
   */
  cylindoErrorImageUrl: PropTypes.string,

  /**
   * Cylindo product feature overrides — later items override earlier ones for the
   * same feature key (text before ':'). Processed into a comma-separated
   * configuration string passed to <cylindo-viewer>.
   * e.g. ["UPHOLSTERY:BX-02", "UPHOLSTERY:AX-11"] → "UPHOLSTERY:AX-11"
   */
  cylindoFeatures: PropTypes.arrayOf(PropTypes.string),

  /**
   * Cylindo product code — mapped to the `code` prop on <cylindo-viewer>.
   */
  cylindoId: PropTypes.string,

  /**
   * Cylindo customer id — mapped to `customer-id` on <cylindo-viewer>.
   */
  customerId: PropTypes.string,

  /**
   * When true, hide Cylindo overlay buttons (Drag to rotate, Zoom, Fullscreen).
   */
  hideOverlayButtons: PropTypes.bool,

  /**
   * Determines whether the first image uses lazy loading (subsequent are always lazy).
   */
  lazy: PropTypes.bool,

  /**
   * Array of PIM image/video objects. When non-empty, the Swiper carousel is shown
   * instead of the Cylindo viewer.
   * Shape: { link, disBaseLink?, alt?, title?, mediaType?: 'video', videoUrl? }
   */
  pimImages: PropTypes.arrayOf(
    PropTypes.shape({
      alt: PropTypes.string,
      disBaseLink: PropTypes.string,
      link: PropTypes.string,
      mediaType: PropTypes.string,
      title: PropTypes.string,
      videoUrl: PropTypes.string,
    })
  ),

  /**
   * Used in accessible aria-label for the gallery / 3D viewer regions.
   */
  productName: PropTypes.string,

  /**
   * When true, suppresses the native Cylindo AR button and renders the custom-styled
   * CylindoARLink button instead (async AR availability check via Cylindo Content API).
   */
  showArButton: PropTypes.bool,

  /**
   * Controls the max-width of the gallery via the theme size variants.
   */
  size: PropTypes.string,
};

export default ImageGalleryV2;
